import {Component, OnInit} from '@angular/core';
import {
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexTooltip,
} from 'ng-apexcharts';
import {FunnelPerformanceTotals, FunnelPerformanceItem, FunnelPerformanceUtmItem} from '../../../models/FunnelPerformance';
import {FunnelSequencePerformanceItem} from '../../../models/FunnelSequencePerformance';
import {AnalyticsService} from '../../../shared/services/analytics.service';
import {IntegrationsService} from '../../../shared/services/integrations.service';
import {UtilService} from '../../../shared/services/util.service';
import {Integration} from '../../../models/Integration';
import {forkJoin, timer} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';

export type DonutChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  colors: string[];
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  tooltip: ApexTooltip;
};

@Component({
  selector: 'app-funnel-performance',
  templateUrl: './funnel-performance.component.html',
  styleUrls: ['./funnel-performance.component.scss']
})
export class FunnelPerformanceComponent implements OnInit {

  fastFilters = [
    { id: 'today', text: 'Hoje' },
    { id: 'yesterday', text: 'Ontem' },
    { id: '7days', text: '7 dias' },
    { id: '15days', text: '15 dias' },
    { id: 'actualMonth', text: 'Mês atual' },
    { id: 'lastMonth', text: 'Mês anterior' },
  ];

  fastFilter: string | null = null;
  integrationId = '0';
  startDate: Date | null = null;
  endDate: Date | null = null;

  integrations: Integration[] = [];

  totals: FunnelPerformanceTotals = {
    total_revenue: 0,
    total_cost: 0,
    total_roi: 0,
    total_conversions: 0,
    total_sms_sent: 0,
    sms_unit_cost: 0,
    total_sms_revenue: 0,
    total_sms_cost: 0,
    total_sms_conversions: 0,
    total_sms_roi: 0,
    total_funnel_sms_sent: 0,
  };

  funnels: FunnelPerformanceItem[] = [];

  salesUtm: FunnelPerformanceUtmItem[] = [];
  utmColors = ['#00BE9A', '#4DA8DA', '#7C6CF0', '#FFD93D', '#FF6B6B', '#FF9F43', '#A29BFE', '#FD79A8'];

  utmPage = 1;
  utmPageSize = 10;
  utmTotalPages = 1;

  selectedFunnel: FunnelPerformanceItem | null = null;
  selectedSequences: FunnelSequencePerformanceItem[] = [];
  loading = true;
  loadingSequences = false;

  pieChartOptions: Partial<DonutChartOptions>;
  utmChartOptions: Partial<DonutChartOptions>;

  constructor(
    private analyticsService: AnalyticsService,
    private integrationService: IntegrationsService,
    private route: ActivatedRoute,
    private router: Router,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.integrationService.listAnalytics().subscribe(integrations => {
      this.integrations = integrations;

      const saved = this.loadSavedFilters();

      if (saved?.fast_filter) {
        this.integrationId = saved.integration_id || '0';
        this.applyFastFilter(saved.fast_filter);
      } else if (saved) {
        this.integrationId = saved.integration_id || '0';
        this.setStartDate(saved.start_date);
        this.setEndDate(saved.end_date);
        this.applyFilter();
      } else {
        this.applyFastFilter('7days');
      }
    });
  }

  setStartDate(date: string): void {
    if (date) {
      this.startDate = this.utilService.isoToDate(date);
    }
  }

  setEndDate(date: string): void {
    if (date) {
      this.endDate = this.utilService.isoToDate(date);
    }
  }

  doFilter(): void {
    const startDate = this.utilService.dateToIso(this.startDate);
    const endDate = this.utilService.dateToIso(this.endDate);

    this.loading = true;
    this.selectedFunnel = null;
    this.selectedSequences = [];

    const integrationId = this.integrationId !== '0' ? this.integrationId : undefined;

    forkJoin([
      this.analyticsService.getFunnelPerformance(startDate, endDate, integrationId),
      timer(1000)
    ]).subscribe({
      next: ([data]) => {
        this.totals = data.totals;
        this.funnels = data.funnels;
        this.salesUtm = data.sales_utm || [];
        this.utmPage = 1;
        this.utmTotalPages = Math.max(1, Math.ceil(this.salesUtm.length / this.utmPageSize));
        this.buildPieChart();
        this.buildUtmChart();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  applyFastFilter(filterId: string): void {
    if (this.fastFilter === filterId) {
      return;
    }

    this.fastFilter = filterId;
    const today = new Date();
    const startDate = new Date(today);
    const endDate = new Date(today);

    const filterFunctions: Record<string, () => void> = {
      yesterday: () => {
        startDate.setDate(today.getDate() - 1);
        endDate.setDate(today.getDate() - 1);
      },
      '7days': () => startDate.setDate(today.getDate() - 7),
      '15days': () => startDate.setDate(today.getDate() - 15),
      actualMonth: () => startDate.setDate(1),
      lastMonth: () => {
        startDate.setDate(1);
        startDate.setMonth(today.getMonth() - 1);
        endDate.setDate(0);
      }
    };

    filterFunctions[filterId]?.();

    this.startDate = startDate;
    this.endDate = endDate;

    this.applyFilter();
  }

  applyFilter(): void {
    let startDate = this.utilService.dateToIso(this.startDate);
    let endDate = this.utilService.dateToIso(this.endDate);
    if (!startDate && endDate) {
      startDate = endDate;
    }
    if (!endDate && startDate) {
      endDate = startDate;
    }
    this.setStartDate(startDate);
    this.setEndDate(endDate);

    this.saveFilters(startDate, endDate);
    this.doFilter();

    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {
        integration_id: this.integrationId !== '0' ? this.integrationId : null,
        start_date: startDate,
        end_date: endDate
      }
    });
  }

  clearFilter(): void {
    this.integrationId = '0';
    this.selectedFunnel = null;
    this.selectedSequences = [];
    this.applyFastFilter('7days');
  }

  selectFunnel(item: FunnelPerformanceItem): void {
    if (this.selectedFunnel?.campaign_id === item.campaign_id) {
      this.selectedFunnel = null;
      this.selectedSequences = [];
      return;
    }
    this.selectedFunnel = item;
    this.loadingSequences = true;

    const startDate = this.utilService.dateToIso(this.startDate);
    const endDate = this.utilService.dateToIso(this.endDate);

    this.analyticsService.getFunnelSequences(item.campaign_id, startDate, endDate)
      .subscribe(sequences => {
        this.selectedSequences = sequences;
        this.loadingSequences = false;
      });
  }

  closeDrillDown(): void {
    this.selectedFunnel = null;
    this.selectedSequences = [];
  }

  getUtmColor(index: number): string {
    return this.utmColors[index % this.utmColors.length];
  }

  getUtmTotal(): number {
    return this.salesUtm.reduce((sum, u) => sum + u.amount, 0);
  }

  getUtmTotalQuantity(): number {
    return this.salesUtm.reduce((sum, u) => sum + u.quantity, 0);
  }

  getUtmBarWidth(item: FunnelPerformanceUtmItem): number {
    const total = this.getUtmTotal();
    return total > 0 ? (item.amount / total) * 100 : 0;
  }

  paginatedUtm(): FunnelPerformanceUtmItem[] {
    const start = (this.utmPage - 1) * this.utmPageSize;
    return this.salesUtm.slice(start, start + this.utmPageSize);
  }

  utmNextPage(): void {
    if (this.utmPage < this.utmTotalPages) {
      this.utmPage++;
    }
  }

  utmPrevPage(): void {
    if (this.utmPage > 1) {
      this.utmPage--;
    }
  }

  getUtmRoi(item: FunnelPerformanceUtmItem): number | null {
    if (item.utm === 'SEM_UTM' || this.totals.total_sms_cost <= 0) {
      return null;
    }
    const utmCost = this.totals.total_sms_cost * (item.quantity / this.salesUtm.reduce((s, u) => s + u.quantity, 0));
    return utmCost > 0 ? item.amount / utmCost : 0;
  }

  private saveFilters(startDate: string, endDate: string): void {
    const data = {
      integration_id: this.integrationId,
      fast_filter: this.fastFilter,
      start_date: startDate,
      end_date: endDate,
      saved_at: Date.now()
    };
    localStorage.setItem('SMSFUNNEL::FUNNEL_FILTERS', JSON.stringify(data));
  }

  private loadSavedFilters(): { integration_id: string; fast_filter: string; start_date: string; end_date: string } | null {
    const raw = localStorage.getItem('SMSFUNNEL::FUNNEL_FILTERS');
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (Date.now() - data.saved_at > 24 * 60 * 60 * 1000) {
      localStorage.removeItem('SMSFUNNEL::FUNNEL_FILTERS');
      return null;
    }

    return data;
  }

  buildUtmChart(): void {
    if (!this.salesUtm.length) {
      this.utmChartOptions = null;
      return;
    }

    this.utmChartOptions = {
      series: this.salesUtm.map(u => u.amount),
      chart: {
        type: 'donut',
        height: 280,
        toolbar: { show: false },
        foreColor: 'rgba(255,255,255,0.7)',
        background: 'transparent',
      },
      labels: this.salesUtm.map(u => u.utm),
      colors: this.salesUtm.map((_, i) => this.getUtmColor(i)),
      legend: {
        position: 'bottom',
        fontSize: '12px',
        labels: { colors: 'rgba(255,255,255,0.6)' },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: '11px', fontWeight: 600 },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
          },
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
      },
    };
  }

  buildPieChart(): void {
    if (!this.funnels.length) {
      this.pieChartOptions = null;
      return;
    }

    this.pieChartOptions = {
      series: this.funnels.map(f => f.revenue),
      chart: {
        type: 'donut',
        height: 280,
        toolbar: { show: false },
        foreColor: 'rgba(255,255,255,0.7)',
        background: 'transparent',
      },
      labels: this.funnels.map(f => f.campaign_name),
      colors: ['#00BE9A', '#7C6CF0', '#FF6B6B', '#FFD93D', '#4DA8DA'],
      legend: {
        position: 'bottom',
        fontSize: '12px',
        labels: { colors: 'rgba(255,255,255,0.6)' },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: '11px', fontWeight: 600 },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
          },
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
      },
    };
  }
}
